import { LEGACY_SECTION_NAMES, Section } from './Section';
// Through the barrel, NOT './CustomMarker' directly. UserProfile is exported
// before PanelRange in index.ts, and CustomMarkerRange extends PanelRange at
// module-evaluation time — reaching that module directly from here makes this
// file the entry of the cycle and BloodworkRange evaluates against an undefined
// PanelRange. Nothing here needs the class until a constructor actually runs,
// by which point the barrel has finished. Same pattern as Bloodwork.ts.
import {
  CustomMarkerDefinition,
  CustomMarkerSection,
  RangeOverride,
  isUsableRangeOverride,
} from '.';
import { bwhLabsVisible } from '../utils/supplementsVisibility';
// Direct, not through the barrel: unitConversions imports nothing, so it can
// never participate in the cycle described above.
import { UnitSystem } from './units/unitConversions';

export type MarkersOutsideOptimalDisplay =
  | 'showBars'
  | 'hideBars'
  | 'hideSection'
  | 'minimizeSection';

/**
 * Keys for UserProfile.enabledLabIntegrations.
 *
 * The STORED value, so it is frozen once a practitioner has ticked the box:
 * renaming it silently revokes every existing opt-in, and the practitioner
 * would find out when results stopped arriving. The client-facing name can
 * change freely, and has (the Profile block was "LabSmarts Practitioner ID"
 * before it became "Virtual Clinic Integration"). This key must not follow it.
 *
 * The same string is what the ingest endpoint will check for, so it is shared
 * vocabulary with the server rather than a UI detail.
 */
export const LAB_INTEGRATION_VIRTUAL_CLINIC = 'virtualClinic';

export class UserProfile {
  id: string; // identity sub
  email: string;
  emailAlias: string;
  firstName: string; // Currently only available in PROD (different Auth strategy)
  lastName: string; // Currently only available in PROD (different Auth strategy)
  phoneNumber: string;
  userName: string;
  signupDate: string; // ISO-8601 format
  // Free-text answers from Create Account, copied off the Cognito attributes
  // custom:practitioner_type / custom:referral_source by postSignUp. Absent
  // for anyone who signed up before the questions existed.
  practitionerType?: string;
  referralSource?: string;
  preparedBy?: string; // Optional field for "Prepared by" text

  // The heading on each PDF's cover page, set in Report Settings. Undefined (a
  // profile saved before the fields existed, or one that never changed them)
  // prints the shipped default — see resolveAnalysisReportTitle() and
  // resolveComparisonReportTitle() in models/reportTitles, which every reader
  // goes through rather than reading these directly.
  analysisReportTitle?: string;
  comparisonReportTitle?: string;

  // User Limitations
  numTests: number; // default is 0
  numTestsAllowed: number; // "unlimited" - the value should be 99,999 in DB, default is 2
  pdfExportAllowed: boolean;
  pdfExportComparisonTableAllowed: boolean;
  editClientAllowed: boolean;
  editTestResultsAllowed: boolean;
  showClientComparisonTable: boolean;
  showAnalyzeResultsComparison: boolean;
  uploadTestResults: boolean;
  showUploadTestResults: boolean;
  customLogoUrl: string;
  customLogoAllowed: boolean;

  // Entitlement for the custom-marker feature — My Markers and the per-test
  // "Add Your Own Markers" table.
  //
  // Defaults TRUE, and postSignUp sets it true, so every practitioner has it on
  // sign-up and it is turned OFF for anyone who doesn't take the paid plan.
  // That direction matters for the default: a profile saved before this field
  // existed has no value for it, and those practitioners must not be locked out
  // of markers they may already be using.
  customMarkersAllowed: boolean = true;

  // Entitlement for My Ranges — setting your own reference ranges for markers
  // LabSmarts already ships.
  //
  // Its own field rather than riding on customMarkersAllowed: the two features
  // are sold separately and do opposite things (My Markers ADDS a marker, My
  // Ranges argues with one that exists). Defaults TRUE and postSignUp sets it
  // true, for the same reason customMarkersAllowed does: a profile saved
  // before this field existed has no value for it and must not be locked out.
  customRangesAllowed: boolean = true;

  enabledSupplementBrands: string[] = ['systemicFormulas'];

  /**
   * Outside systems this practitioner has allowed to WRITE test results into
   * their account. Empty (the default) means none, and that is the whole
   * security model on the LabSmarts side.
   *
   * An IAM credential proves a caller is Virtual Clinic. It does not prove
   * Virtual Clinic may write into a given practitioner's charts, and the
   * practitionerId travels in the request body — so without this list, anyone
   * holding those credentials could push a result into any practitioner's
   * account given only a known id. Consent is per practitioner and lives here.
   *
   * A LIST rather than a boolean so the second integration costs no new field
   * and no migration. The ingest endpoint's check is one line:
   *
   *   (profile.enabledLabIntegrations || []).includes(LAB_INTEGRATION_*)
   *
   * which also answers a profile saved before the field existed (undefined
   * reads as off) and revocation (unticking removes the entry, and the next
   * push is refused). Nothing here is an entitlement: this is the
   * practitioner's own consent, so it is theirs to write through the ordinary
   * profile PUT, and entitlementFields.js deliberately does not list it.
   */
  enabledLabIntegrations: string[] = [];

  // Brands the app has already auto-added to enabledSupplementBrands once (see
  // the constructor). A brand is seeded a single time so a newly-launched brand
  // reaches practitioners whose stored profile predates it; after that the
  // practitioner's own checkbox choice wins, including unchecking it.
  seededSupplementBrands: string[] = [];

  // Sections to exclude from the Analysis PDF by default. Empty (the default)
  // means every section is included. Each section header in Test Results Analysis
  // has its own "Include in PDF" checkbox; this list seeds those checkboxes on
  // mount. Per-test toggles are session-only and do not persist to this list.
  excludedFromPdfSections: Section[] = [];

  // Sections whose Insights PANEL is left out of the Analysis PDF by default,
  // while the section itself still prints its bar graphs. Empty (the default)
  // means every printing panel prints.
  //
  // A second, narrower list rather than a mode on the one above, because the two
  // answer different questions and a practitioner sets them independently: the
  // section is either in the client's report or it isn't, and separately the
  // interpretation above its bars is either something they want the client
  // reading or something they keep for the consult. Some practitioners want the
  // charts alone.
  //
  // Screen-only sections are unaffected: this list is read by the PDF, never by
  // the analysis page, so the practitioner keeps every panel on screen whatever
  // is ticked here. Seeds the per-section "Include Insights Panel in PDF"
  // checkbox, which is per-test and session-only like its neighbour.
  excludedInsightsFromPdfSections: Section[] = [];

  // Controls how the "Markers Outside Optimal" section renders by default:
  //   showBars        - the section is open, panels showing reference-range bars
  //                     (default)
  //   hideBars        - the section is open with each panel's charts collapsed;
  //                     user can click a panel's chevron to reveal its bars for
  //                     that session
  //   hideSection     - the entire section is hidden in the UI
  //   minimizeSection - the section renders collapsed to its header; clicking it
  //                     expands to the 'showBars' layout
  //
  // All four are screen-only. The PDF renders the section for every mode except
  // 'hideSection' (see bloodwork-analysis-pdf.tsx) — a minimized section still
  // exports in full, and the per-section "Include in PDF" checkbox is what
  // controls that.
  markersOutsideOptimalDisplay: MarkersOutsideOptimalDisplay = 'showBars';

  // When true (default), the Inflammation section hides cross-section
  // markers (those with a home in another section, e.g. ferritin, GGT,
  // albumin, HDL) unless they contribute a signal there. Markers native
  // to the section (hs-CRP, ESR, homocysteine) always stay visible
  // regardless of value. If filtering empties the section, it disappears
  // entirely.
  showOnlySectionSignals: boolean = true;

  // When true (default), the Immune section hides cross-section markers
  // unless they contribute a signal there. Example: alkaline phosphatase
  // is hidden when optimal or high (only low Alk Phos signals low zinc,
  // which the immune system needs); globulin is hidden when optimal.
  // Markers native to the section (WBC and differentials) always stay
  // visible regardless of value.
  showOnlyImmuneSectionSignals: boolean = true;

  // When true (default), the Immune section shows the absolute differential
  // bar graphs (Absolute Neutrophils, Lymphocytes, Monocytes, Eosinophils,
  // Basophils). Uncheck to hide those bar graphs. This is independent of
  // showOnlyImmuneSectionSignals, which never hides the differentials.
  showImmuneAbsolutes: boolean = true;

  // When true (default), a comparison report (current vs a previous test) also
  // renders reference-range bars for markers that have a value in the PREVIOUS
  // test but NOT the current one, plotting just the previous (blue) blood drop.
  // This gives a complete side-by-side even when the current test dropped a
  // marker — otherwise those markers vanish from the charts while still driving
  // the Results Overview section colors and thumbs. Uncheck to show only
  // markers present in the current test. No effect when viewing a single test.
  showPreviousTestMarkersInComparison: boolean = true;

  // When true (default), the Test Results Comparison page shows the Notes
  // column beside each table. Uncheck to take the column off the screen
  // entirely, which hands its width back to the marker columns.
  //
  // Independent of showComparisonNotesInPdf on purpose: a practitioner who
  // works on paper turns the screen column off and leaves the PDF one on, so
  // the printout carries blank boxes to write in. Notes already saved are not
  // touched — they keep printing, and reappear if the column is switched back
  // on.
  showComparisonNotesOnScreen: boolean = true;

  // When true (default), "Export to PDF w/ notes" prints the comparison
  // tables' Notes column on EVERY section, whether or not a note was typed:
  // the blank ones are writing room for a practitioner who prints the report
  // on paper. Uncheck to leave the column out of the PDF entirely, typed
  // notes included. "Export to PDF w/o notes" never prints it either way.
  showComparisonNotesInPdf: boolean = true;

  // The two note BLOCKS the comparison PDF prints after the tables, each
  // independent of the Notes column above and of each other. All three used to
  // ride on the one "with notes" export, so a practitioner who wanted the
  // per-section Notes column had to take the other two as well.
  //
  // "Insights & Section Notes by Collection Date": the table of each test's
  // Insights note and its per-section notes, one row per collection date.
  showComparisonInsightsNotesInPdf: boolean = true;

  // "Overall Progress & Trends": the client-level card
  // (overallPractitionerNotes) at the end of the report.
  showComparisonOverallProgressInPdf: boolean = true;

  // Where the "Hide Alarm" and "Hide Lab" checkboxes in the Test Results
  // Comparison table header START on each report. True means that pair of
  // range columns (Alarm Min/Max, Lab Min/Max) is already hidden when the
  // comparison opens. Optimal Min/Max is not a setting: it always shows.
  //
  // Both default FALSE, which is what the table has always done, so nothing
  // changes for a profile saved before these existed. This sets the starting
  // position only: the header checkboxes still drop or restore the columns
  // for the report on screen, and the PDF prints whatever the header shows.
  hideAlarmColumnsInComparison: boolean = false;
  hideLabColumnsInComparison: boolean = false;

  // The practitioner's default unit system for markers: 'US' conventional
  // (mg/dL, ng/mL) or 'SI' international (mmol/L, umol/L, g/L), which is how
  // labs report outside the US — Canada and Australia especially.
  //
  // A default, not a rule: the per-client Units radio on Add/Edit Client
  // overrides it, so a practice with clients in both systems is served. See
  // resolveUnitSystem() for the precedence.
  //
  // Display-only. Every value and every reference range is stored in US
  // conventional units regardless of this setting, and conversion happens at
  // the entry and display boundaries — see units/unitConversions.ts.
  unitSystem: UnitSystem = 'US';

  // The practitioner's own marker definitions ("My Markers", under the Test
  // Results menu), for markers LabSmarts does not ship yet. Offered as a pick
  // list on Add Test Results so the ranges are entered once, not per client.
  //
  // Private to this practitioner — nothing here reaches the shared
  // BloodworkRange catalog. Picking one COPIES it onto the test
  // (Bloodwork.customMarkers), so editing or deleting a definition never
  // changes a test that has already been saved.
  customMarkerLibrary: CustomMarkerDefinition[] = [];

  // The practitioner's own reference ranges ("My Ranges", under the Test
  // Results menu), for markers LabSmarts DOES ship. One entry per population
  // row they have changed, carrying only the numbers they changed.
  //
  // Unlike customMarkerLibrary, nothing is copied onto a test: an analysis
  // applies these to the range table every time a report is opened, so a
  // change here rewrites every report this practitioner has ever produced,
  // backwards included. That is deliberate and the save dialog says so — a
  // practitioner who corrects a range they believe is wrong should not have to
  // re-open and re-save every client for it to count. See my-ranges-spec.md.
  rangeOverrides: RangeOverride[] = [];

  // Sections the practitioner named themselves, for panels LabSmarts has no
  // home for ("IgG Allergens", "Mycotoxins"). Offered alongside the shipped
  // sections wherever a custom marker is filed, and printed after them as plain
  // reference-range sections under the name given here (practitionerSectionTitle).
  //
  // A marker records the section's NAME, not its id, so renaming or deleting a
  // section here cannot retitle a report already issued to a client — the same
  // snapshot rule customMarkerLibrary follows.
  customMarkerSections: CustomMarkerSection[] = [];

  // The sections whose markers load onto EVERY new test, so a practitioner who
  // runs the same add-on panels does not rebuild the same table per client.
  //
  // Stores the section VALUE the dropdown uses ('' for the appendix, a Section
  // for a LabSmarts heading, 'practitioner:Name' for one of their own) rather
  // than a section id, because two of those three kinds have no id to store.
  //
  // Membership is resolved against customMarkerLibrary when a test is opened,
  // never stored here: a marker added to a defaulted section afterwards has to
  // join it with no second step, which a stored marker list could not do.
  defaultTestSections: string[] = [];

  // Individual markers that load onto every new test, by CustomMarkerDefinition
  // id.
  //
  // Separate from defaultTestSections, and a UNION with it: a practitioner who
  // runs a five-marker lipid panel but only ever adds NON HDL CHOLESTEROL has
  // no section to tick, and ticking the section would bring the other four.
  // Ids rather than names, because the name is the thing most likely to be
  // corrected and the id is what survives it.
  defaultTestMarkerIds: string[] = [];

  // Sections whose markers show their full reference range on Add Test Results
  // instead of folding to name, result and units.
  //
  // Folding suits a panel entered from memory; a practitioner who checks each
  // range as they type wants it in front of them, and clicking a caret per
  // marker is not an answer. Stored as section values, like defaultTestSections.
  expandedTestSections: string[] = [];

  canAddTest(): boolean {
    if (this.numTests < this.numTestsAllowed) {
      return true;
    }
    return false;
  }

  canUploadPdf(): boolean {
    return this.uploadTestResults;
  }

  /**
   * Whether this practitioner has allowed `integration` to write results into
   * their account. The mirror of the one-line check the ingest endpoint makes,
   * and the same answer for a profile saved before the field existed: no.
   */
  allowsLabIntegration(integration: string): boolean {
    return (this.enabledLabIntegrations || []).indexOf(integration) > -1;
  }

  canSeeUploadTestResults(): boolean {
    return this.showUploadTestResults;
  }

  canUploadCustomLogo(): boolean {
    return this.customLogoAllowed;
  }

  /**
   * Whether this practitioner can ADD custom markers. Existing definitions and
   * markers already on saved tests keep rendering either way — turning the
   * feature off must never blank a report that has already been issued.
   */
  canUseCustomMarkers(): boolean {
    return this.customMarkersAllowed !== false;
  }

  /**
   * Whether this practitioner can SET their own reference ranges.
   *
   * Turning it off stops existing overrides applying, which is the opposite of
   * canUseCustomMarkers(): there, revoking would blank part of a report that
   * has already been issued, whereas here it reverts to a complete LabSmarts
   * report. The stored overrides are kept either way, so a re-purchase
   * restores them with nothing re-entered.
   */
  canUseCustomRanges(): boolean {
    return this.customRangesAllowed !== false;
  }

  /** The overrides that should actually be applied, entitlement included. */
  activeRangeOverrides(): RangeOverride[] {
    if (!this.canUseCustomRanges()) {
      return [];
    }
    return this.rangeOverrides || [];
  }

  hasCustomLogo(): boolean {
    return (
      this.canUploadCustomLogo() &&
      !!this.customLogoUrl &&
      this.customLogoUrl.trim() !== ''
    );
  }

  getLogoUrl(): string {
    return this.canUploadCustomLogo() && this.hasCustomLogo()
      ? this.customLogoUrl
      : '/assets/imgs/logo.png';
  }

  incrementNumTests(): void {
    this.numTests++;
  }

  constructor(userProfile?: UserProfile) {
    // This handles copying the simple properties
    if (!userProfile) {
      return;
    }
    // This handles copying the simple properties
    Object.keys(userProfile).forEach(key => {
      this[key] = userProfile[key];
    });

    // Every profile stored before this field existed copies `undefined` over
    // the class default, so normalize rather than trusting the initializer.
    // Anything unrecognized reads as US, which is what those accounts see now.
    this.unitSystem = userProfile.unitSystem === 'SI' ? 'SI' : 'US';

    // What excludedFromPdfSections persists is the enum's VALUE, which for the
    // custom-marker section is client-facing copy that has now been renamed
    // three times (see Section.CUSTOM_MARKERS). A practitioner who turned that
    // section off under an older name has that older string stored; left as-is
    // it would match no section and the section would quietly reappear in the
    // client's PDF. Remap every retired name on load.
    //
    // No other Section shares these strings, so nothing else is caught by the
    // remap — the "Additional Markers" card in Add Test Results is a panel of
    // shipped markers, not a Section, and never reaches this list. Practitioner
    // sections are keyed off a frozen prefix (practitionerSectionStateKey) and
    // need no remap at all.
    const legacyBySection = new Map<string, Section>();
    Object.keys(LEGACY_SECTION_NAMES).forEach(current =>
      LEGACY_SECTION_NAMES[current].forEach(legacy =>
        legacyBySection.set(legacy, current as Section),
      ),
    );
    this.excludedFromPdfSections = Array.isArray(
      userProfile.excludedFromPdfSections,
    )
      ? userProfile.excludedFromPdfSections.map(
          section => legacyBySection.get(section as string) || section,
        )
      : [];

    // Same normalization for the Insights-panel list: tolerate a profile saved
    // before the field existed, and run the same rename remap. No renamed
    // section prints an Insights panel today, so the remap catches nothing yet —
    // it is there because the next rename should not have to find this line.
    this.excludedInsightsFromPdfSections = Array.isArray(
      userProfile.excludedInsightsFromPdfSections,
    )
      ? userProfile.excludedInsightsFromPdfSections.map(
          section => legacyBySection.get(section as string) || section,
        )
      : [];

    // Rehydrate library definitions as real instances, and tolerate a profile
    // saved before this field existed (or any non-array shape).
    this.customMarkerLibrary = Array.isArray(userProfile.customMarkerLibrary)
      ? userProfile.customMarkerLibrary.map(
          (definition: any) => new CustomMarkerDefinition(definition),
        )
      : [];

    // Same tolerance as the library above, and one addition: a half-written
    // override (no scope key, or no number on it) is dropped rather than kept,
    // because it would apply nothing and still occupy a row on the page.
    this.rangeOverrides = Array.isArray(userProfile.rangeOverrides)
      ? userProfile.rangeOverrides
          .map((override: any) => new RangeOverride(override))
          .filter(override => isUsableRangeOverride(override))
      : [];

    this.customMarkerSections = Array.isArray(userProfile.customMarkerSections)
      ? userProfile.customMarkerSections
          .map((section: any) => new CustomMarkerSection(section))
          .filter(section => !!(section.id && (section.name || '').trim()))
      : [];

    // Tolerates a profile saved before this field existed, and drops anything
    // that is not a usable section value — a stray entry would otherwise load
    // nothing onto every test forever, silently.
    this.defaultTestSections = Array.isArray(userProfile.defaultTestSections)
      ? userProfile.defaultTestSections.filter(
          (value: any) => typeof value === 'string',
        )
      : [];

    this.defaultTestMarkerIds = Array.isArray(userProfile.defaultTestMarkerIds)
      ? userProfile.defaultTestMarkerIds.filter(
          (value: any) => typeof value === 'string',
        )
      : [];

    // Every profile saved before this field existed copies `undefined` over the
    // class default, so normalize rather than trusting the initializer — an
    // undefined list would make allowsLabIntegration() throw on the `indexOf`
    // rather than answer "no", and "no" is the answer that matters here. Same
    // fail-closed direction for a stored value of any other shape.
    this.enabledLabIntegrations = Array.isArray(
      userProfile.enabledLabIntegrations,
    )
      ? userProfile.enabledLabIntegrations.filter(
          (value: any) => typeof value === 'string' && value.trim() !== '',
        )
      : [];

    this.expandedTestSections = Array.isArray(userProfile.expandedTestSections)
      ? userProfile.expandedTestSections.filter(
          (value: any) => typeof value === 'string',
        )
      : [];

    // BWH Labs is a pre-launch brand, on hold until the partnership and its
    // affiliate code are finalized: hidden everywhere today, local dev included
    // (see bwhLabsVisible()), mirroring the Supplement Settings block.
    // Because any prior profile save persists enabledSupplementBrands, the
    // field default can't be relied on to reach existing profiles — so
    // normalize on load:
    //   hold on (today) → strip 'bwhLabs' so it never renders, even if a stored
    //                     profile happens to carry it (dev and slava profiles
    //                     do: the brand shipped there before this hold).
    //   hold lifted     → seed 'bwhLabs' ONCE so it renders for every
    //                     practitioner
    // The seed is one-time and recorded in seededSupplementBrands: re-adding it
    // on every load would silently undo a practitioner who unchecks the brand,
    // so once seeded the stored enabledSupplementBrands is left alone.
    //
    // Stripping also clears that seed marker, which matters for the profiles
    // seeded while the brand still shipped: leaving the marker behind would
    // mean nobody gets re-seeded when the hold lifts — every practitioner
    // would find the brand silently unchecked at launch.
    const showBwhLabs = bwhLabsVisible();
    if (!this.enabledSupplementBrands) {
      this.enabledSupplementBrands = ['systemicFormulas'];
    }
    if (!this.seededSupplementBrands) {
      this.seededSupplementBrands = [];
    }
    if (showBwhLabs) {
      if (
        !this.enabledSupplementBrands.includes('bwhLabs') &&
        !this.seededSupplementBrands.includes('bwhLabs')
      ) {
        this.enabledSupplementBrands = [
          ...this.enabledSupplementBrands,
          'bwhLabs',
        ];
      }
      if (!this.seededSupplementBrands.includes('bwhLabs')) {
        this.seededSupplementBrands = [
          ...this.seededSupplementBrands,
          'bwhLabs',
        ];
      }
    } else {
      this.enabledSupplementBrands = this.enabledSupplementBrands.filter(
        b => b !== 'bwhLabs',
      );
      this.seededSupplementBrands = this.seededSupplementBrands.filter(
        b => b !== 'bwhLabs',
      );
    }
  }
}
